import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { request } from './client.ts';
import { qk } from './keys.ts';
import type { ScreenGroup, CreateGroup, UpdateGroup, SetGroupMembers } from '@seencast/shared';
import type { ListEnvelope } from './client.ts';

// Groups carry `overlay_id` (now on the shared ScreenGroup schema, Sprint 4):
//   null           → the group imposes no overlay; member screens fall through to
//                    the org default (unless they set their own screen-level one)
//   <overlay id>   → a specific overlay for every screen in the group
// Explicit per-member "no overlay" opt-out stays deferred, matching per-screen.

export function listGroups(): Promise<ScreenGroup[]> {
  return request<ListEnvelope<ScreenGroup>>('/groups').then((r) => r.items);
}

export function useGroups() {
  return useQuery({ queryKey: qk.groups, queryFn: listGroups });
}

function invalidate(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: qk.groups });
  qc.invalidateQueries({ queryKey: ['screens'] }); // group chips live on screens too
}

export function useCreateGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateGroup) => request<ScreenGroup>('/groups', { method: 'POST', body }),
    onSuccess: () => invalidate(qc),
  });
}

export function useUpdateGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateGroup }) =>
      request<ScreenGroup>(`/groups/${id}`, { method: 'PATCH', body }),
    onSuccess: () => invalidate(qc),
  });
}

export function useDeleteGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => request<void>(`/groups/${id}`, { method: 'DELETE' }),
    onSuccess: () => invalidate(qc),
  });
}

export function useSetGroupMembers() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: SetGroupMembers }) =>
      request<ScreenGroup>(`/groups/${id}/members`, { method: 'PUT', body }),
    onSuccess: () => invalidate(qc),
  });
}

/** Set (or clear → null) a group's overlay. PATCH /v1/groups/:id {overlay_id}. */
export function useSetGroupOverlay() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, overlay_id }: { id: string; overlay_id: string | null }) =>
      request<ScreenGroup>(`/groups/${id}`, { method: 'PATCH', body: { overlay_id } }),
    onSuccess: () => invalidate(qc),
  });
}

// Sprint 9 — group overlay tri-state (docs/09 §C2). A group can now explicitly
// opt OUT of any overlay ("No overlay"), which STOPS resolution at NONE rather than
// falling through to the org default. `overlay_id` and `overlay_opt_out` are
// mutually exclusive (the server enforces it; the UI sends the pair so the loser
// is cleared in the same PATCH). Until the shared schema carries `overlay_opt_out`,
// read it defensively off the group.
export type GroupOverlayState =
  | { kind: 'inherit' } // overlay_id null, opt_out false → org default applies
  | { kind: 'none' } //    opt_out true → no overlay, ignore org default
  | { kind: 'overlay'; overlay_id: string };

export function groupOverlayState(g: ScreenGroup): GroupOverlayState {
  if (g.overlay_id) return { kind: 'overlay', overlay_id: g.overlay_id };
  if (g.overlay_opt_out) return { kind: 'none' };
  return { kind: 'inherit' };
}

interface GroupOverlayBody {
  overlay_id: string | null;
  overlay_opt_out: boolean;
}

/** Apply a tri-state overlay choice to a group in one PATCH. */
export function useSetGroupOverlayState() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, state }: { id: string; state: GroupOverlayState }) => {
      const body: GroupOverlayBody =
        state.kind === 'overlay'
          ? { overlay_id: state.overlay_id, overlay_opt_out: false }
          : state.kind === 'none'
            ? { overlay_id: null, overlay_opt_out: true }
            : { overlay_id: null, overlay_opt_out: false };
      return request<ScreenGroup>(`/groups/${id}`, { method: 'PATCH', body });
    },
    onSuccess: () => invalidate(qc),
  });
}
